import {
    IFilteredEventsResponse,
    IProjectSearchResponse,
    IRecentActivityResponse,
    ICreateEventWithClientRequest,
    ICreateEventWithClientResponse,
    ICreateEventResponse,
    ICreateProject,
    ICreateProjectWithExtendedData,
    IProjectDateResponse,
    TFilteredEventsExtendedResponse,
    IWorkspaceCreateProject,
    INotificationData
} from '~/types/server.types';
import { IEvent, IImage, IMinimalEvent } from '~/types/model.types';
import {
    v2EventDetailsPath,
    v2EventsSummaryPath,
    v2GetHomeRecentActivityPath
} from '~/utils/ts-routes.utils';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { TChangeWorkspaceToggleClientPortalExposureResponse } from '~/apps/main/Workspace/server-types';
import { ClientPendingTaskController } from '~/controllers/client-pending-task.controller';
import { IApiService } from '~/services/interfaces/IApiService';
import {
    ClientPendingTaskType,
    SendRescheduleProjectEmailData
} from '~/common/hooks/ClientPendingTask/client-pending-task.types';

export class ProjectsAdapter extends BaseHttpAdapter {
    clientPendingTaskCtrl: ClientPendingTaskController;

    constructor({
        apiAdapter,
        clientPendingTaskCtrl
    }: {
        apiAdapter: IApiService;
        clientPendingTaskCtrl: ClientPendingTaskController;
    }) {
        super(apiAdapter);

        this.clientPendingTaskCtrl = clientPendingTaskCtrl;
    }

    createProjectWithClient(data: ICreateEventWithClientRequest) {
        return this.apiService.apiRequest<ICreateEventWithClientResponse>({
            method: 'post',
            url: `/api/v2/events/client`,
            data
        });
    }

    createExtProject(data: IWorkspaceCreateProject) {
        return this.apiService.apiRequest<INotificationData>({
            method: 'post',
            url: `/api/v2/ext/events`,
            data
        });
    }

    createProject(
        paramsData?: ICreateProject,
        bodyData?: ICreateProjectWithExtendedData
    ) {
        return this.apiService.apiRequest<ICreateEventResponse>({
            method: 'post',
            url: v2EventsSummaryPath(paramsData),
            data: bodyData
        });
    }

    fetchProject(projectId: string) {
        return this.apiService.apiRequest<IEvent>({
            method: 'get',
            url: v2EventDetailsPath(projectId)
        });
    }

    fetchRecentActivity() {
        return this.apiService.apiRequest<IRecentActivityResponse>({
            method: 'GET',
            url: v2GetHomeRecentActivityPath()
        });
    }

    searchProjectList(query, data?) {
        return this.apiService.apiRequest<IProjectSearchResponse[]>({
            method: 'get',
            url: `/api/v2/search/couple_cards/${query}`,
            params: data
        });
    }

    getAllProjectList(data?) {
        return this.apiService.apiRequest<IProjectSearchResponse[]>({
            method: 'get',
            url: `/api/v2/search/search_all/couple_cards`,
            params: data
        });
    }

    _fetchRecentProjects(data) {
        return this.apiService.apiRequest<TFilteredEventsExtendedResponse>({
            method: 'get',
            url: '/api/v2/filtered_events',
            params: data
        });
    }

    updateProject({
        _id,
        ...updatedData
    }: Partial<IEvent> & Pick<IEvent, '_id'>) {
        return this.apiService.apiRequest<IEvent>({
            method: 'put',
            url: `/api/v2/events/${_id}`,
            data: updatedData
        });
    }

    saveProjectImage({
        projectId,
        image
    }: {
        projectId: string;
        image: IImage;
    }) {
        return this.apiService.apiRequest<{ _id: string; event_image: IImage }>(
            {
                method: 'put',
                url: `/api/v2/events/${projectId}/image`,
                data: { event_image: image }
            }
        );
    }

    updateProjectSpaces(
        eventId: string,
        spaceId: string,
        addOrRemove: 'add' | 'remove'
    ) {
        return this.apiService.apiRequest<IEvent>({
            method: addOrRemove === 'add' ? 'post' : 'delete',
            url: `/api/v2/events/${eventId}/space/${spaceId}`
        });
    }

    createProjectDate(eventId: string) {
        return this.apiService.apiRequest<IProjectDateResponse>({
            method: 'post',
            url: `/api/v2/events/${eventId}/project_dates`
        });
    }

    deleteProjectDate(eventId: string, dateId: string) {
        return this.apiService.apiRequest<IProjectDateResponse>({
            method: 'delete',
            url: `/api/v2/events/${eventId}/project_dates/${dateId}`
        });
    }

    async toggleIsClientPortalExposed(eventId: string, expose: boolean) {
        return this.apiService.apiRequest<TChangeWorkspaceToggleClientPortalExposureResponse>(
            {
                method: 'put',
                url: `/api/v2/events/${eventId}/toggle_client_portal_exposure`,
                data: { expose }
            }
        );
    }

    async sendRescheduleProjectEmail(data: SendRescheduleProjectEmailData) {
        return this.clientPendingTaskCtrl.createClientPendingTask({
            taskType: ClientPendingTaskType.SendRescheduleProjectEmail,
            taskData: data
        });
    }

    async fetchClientEvents(companyId: string) {
        return this.apiService.apiRequest<{ any; data: IMinimalEvent[] }>({
            method: 'get',
            url: '/api/v2/client/events',
            params: {
                filter_company_id: companyId
            }
        });
    }

    async fetchEvents(companyId: string) {
        return this.apiService.apiRequest<IFilteredEventsResponse>({
            method: 'get',
            url: '/api/v2/events',
            params: {
                filter_company_id: companyId
            }
        });
    }
}
